import { AssigneeAvatarPicker } from '@components/assigned-user'
import { CarImageHolder } from '@components/car-image-holder'
import { ConfirmModal, PdfActionMenuItems, pdfActionItems } from '@components/common'
import { CustomItem, ItemList } from '@components/custom-item'
import { EmailForm, InvoiceEmailVariantModal } from '@components/email'
import { FormModal, FormModalFooter } from '@components/branded-modal'
import { Note } from '@components/note'
import { ScoreDrawer } from '@components/score-drawer'
import { AdasBox, AdasCircularProgress, AdasContainer, AdasImage, AdasTypography } from '@components/wrapper-components'
import {
  ArrowLeft,
  Camera,
  CheckCheck,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardCheck,
  FileSignature,
  FileText,
  HelpCircle,
  Info,
  Mail,
  RotateCcw
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
  getInvoiceByReferenceNumber,
  getInvoiceCertificateOfCalibration,
  getInvoiceEmails,
  getInvoicePdf,
  reOpenInvoices,
  revertToWorkOrder,
  updateNote,
  updateClient,
  getQWIByInvoiceId,
  getReferralByCode
} from '../../../api/api'
import {
  BUTTONS_DISPLAY_LABEL,
  DATE_FORMATS,
  GENERIC_MESSAGES,
  INVOICE_PDF_VARIANT,
  invoiceVariantsForEmailChoice,
  PARAM_NAMES,
  getWorkOrderSummaryUrl,
  REDIRECT_URLS,
  getUrlByNameAndId
} from '../../../constants'
import { useLoadingStore, useMessageStore, useUserStore } from '../../../store'
import { getFriendlyErrorMessage } from '../../../utils/errorMessage'
import { useMobileToolbarStore } from '@caradasstore/MobileToolbarStore'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { useScrollTop } from '../../../utils/useScrollTop'
import { currencyFormatter, formatDate } from '../../../utils/utils'
import { Logger } from '../../../logger'
import { handlePdf, PDF_ACTION } from '../../../utils/pdfActions'
import { StatusEnum } from '../../../enums/enums'
import ExpandableSection from '@components/custom-item/item-list/ExpandableSection'
import { MobileSection } from '@components/custom-item/item-list/MobileSection'
import { flattenItem, normalizeCustomItems, splitCustomItems } from 'utils/itemHelpers'
import { useRevertedInvoice } from '@caradasstore/InvoiceEditStore'
import { ProductContractBanner, hasContractBanner } from '@components/product'
import { CalibrationLevelLegend, shouldShowLevelLegend } from '@components/calibration-level-legend'
import { CreateSupportRequestModal } from '@pages/support/support-request-modal'

const CalibrationStepRow = ({ label, complete, onClick }) => (
  <button
    type='button'
    onClick={onClick}
    className='flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-[#F8FAFC]'
  >
    <AdasBox className='flex items-center gap-3'>
      <span
        className={`inline-flex h-6 w-6 flex-none items-center justify-center rounded-full ${
          complete ? 'bg-[#16A34A] text-white' : 'border border-[#D1D5D8] bg-white text-[#6B7280]'
        }`}
      >
        {complete && <CheckCircle2 className='h-4 w-4' />}
      </span>
      <span className='text-[14px] font-semibold text-[#0A1628]'>{label}</span>
      <span className={`text-[12px] font-semibold uppercase tracking-wide ${complete ? 'text-[#16A34A]' : 'text-[#6B7280]'}`}>
        {complete ? 'Complete' : 'Incomplete'}
      </span>
    </AdasBox>
    <ChevronRight className='h-4 w-4 text-[#6B7280]' />
  </button>
)

const WoDocStepRow = ({ label, complete, onClick }) => (
  <AdasBox className='flex w-full items-center justify-between gap-3 px-5 py-4'>
    <AdasBox className='flex items-center gap-3'>
      <span
        className={`inline-flex h-6 w-6 flex-none items-center justify-center rounded-full border ${
          complete ? 'border-[#0A1628] text-[#0A1628]' : 'border-[#D1D5D8] text-[#D1D5D8]'
        }`}
      >
        <CheckCircle2 className='h-4 w-4' strokeWidth={2} />
      </span>
      <span className='text-[15px] font-bold text-[#0A1628]'>{label}</span>
    </AdasBox>
    <button type='button' onClick={onClick} className='text-[13px] font-bold uppercase tracking-wide text-[#1F6FEB] hover:underline'>
      Review
    </button>
  </AdasBox>
)

// Decide how the current user may reopen a CLOSED invoice:
//   'blocked' → cannot reopen (manager, invoice closed in a different month)
//   'warn'    → reopen allowed, show the "same month only" reminder (manager, same month)
//   'allow'   → reopen immediately with no month gate
// Owners and admins are intentionally unrestricted so they can reopen invoices
// that were closed months ago; only managers are held to the close-month rule.
export const getReopenRestriction = (user, closedAt, now = new Date()) => {
  if (user?.is_manager && closedAt) {
    const closedMonth = new Date(formatDate(closedAt, DATE_FORMATS.ISO_DATE_STRING))
    return closedMonth.getMonth() !== now.getMonth() ? 'blocked' : 'warn'
  }
  return 'allow'
}

export const InvoiceSummaryPage = () => {
  useScrollTop()
  const { [PARAM_NAMES.REFERENCE_NUMBER]: referenceNumber } = useParams()
  const user = useUserStore((store) => store.currentUser)
  const [packages, setPackages] = useState([]) // Calibrations == Packages
  const [services, setServices] = useState([])
  const [parts, setParts] = useState([])
  const [labor, setLabor] = useState([])
  const [customItemList, setCustomItemList] = useState([])
  const [isScorecardOpen, setIsScorecardOpen] = useState(false)
  const [supportOpen, setSupportOpen] = useState(false)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const addMessage = useMessageStore((store) => store.addMessage)
  const isLoading = useLoadingStore((store) => store.loading)
  const [isServiceOnly, setIsServiceOnly] = useState(false)

  const [wo, setWo] = useState({})
  const [invoiceData, setInvoiceData] = useState({})
  const [isScored, setIsScored] = useState(false)
  const isActive = false
  // eslint-disable-next-line no-unused-vars
  const [pdfLoader, setPdfLoader] = useState(false)
  const [isEmail, setIsEmail] = useState(false)
  const [isEmailVariantPickerOpen, setIsEmailVariantPickerOpen] = useState(false)
  const [emailContacts, setEmailContacts] = useState({})
  const [invoicePdfs, setInvoicePdfs] = useState([])
  const [emailVariants, setEmailVariants] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [showReopenModal, setShowReopenModal] = useState(false)
  const [showNotifyError, setShowNotifyError] = useState(false)
  const [showWarningError, setShowWarningError] = useState(false)
  const [notes, setNotes] = useState({
    notes_external: wo.notes_external || '',
    notes_internal: wo.notes_internal || '',
    client_notes: wo.client?.notes || ''
  })
  const [QWIData, setQWIData] = useState()
  const [referralData, setReferralData] = useState()

  const history = useHistory()

  const setRevertedInvoice = useRevertedInvoice((store) => store.setRevertedInvoice)

  const redirectToForbiddenPage = () => {
    history.replace(REDIRECT_URLS.FORBIDDEN_PAGE)
  }

  const redirectPreCalibration = () => {
    history.push(getUrlByNameAndId(REDIRECT_URLS.INVOICE_PRE_CALIBRATION, referenceNumber))
  }

  const redirectCalibration = () => {
    history.push(getUrlByNameAndId(REDIRECT_URLS.INVOICE_CALIBRATION, referenceNumber))
  }
  const redirectPostCalibration = () => {
    history.push(getUrlByNameAndId(REDIRECT_URLS.INVOICE_POST_CALIBRATION, referenceNumber))
  }
  const redirectVehicleInfo = () => {
    history.push(getUrlByNameAndId(REDIRECT_URLS.INVOICE_VEHICLE_INFO, referenceNumber))
  }
  const redirectDocuments = () => {
    history.push(getUrlByNameAndId(REDIRECT_URLS.INVOICE_DOCUMENT, referenceNumber))
  }

  const redirectPhotos = () => {
    history.push(getUrlByNameAndId(REDIRECT_URLS.INVOICE_PHOTO, referenceNumber))
  }

  const packagesTotal = useMemo(() => {
    return packages.length > 0 ? packages.reduce((total, current) => total + current.total_price, 0) : 0
  }, [packages])

  const servicesTotal = useMemo(() => {
    return services.length > 0 ? services.reduce((total, current) => total + current.total_price, 0) : 0
  }, [services])
  const partsTotal = useMemo(() => {
    return parts.length > 0 ? parts.reduce((total, current) => total + current.total_price, 0) : 0
  }, [parts])
  const laborTotal = useMemo(() => {
    return labor.length > 0 ? labor.reduce((total, current) => total + current.total_price, 0) : 0
  }, [labor])

  const getCustomItemTotal = useCallback(
    (items = []) =>
      items.length > 0
        ? items.reduce((total_price, current) => {
            if (current.type === 'DISCOUNT') {
              return total_price - current.total_price
            }
            return total_price + current.total_price
          }, 0)
        : 0,
    []
  )
  const customItemTotal = useMemo(() => getCustomItemTotal(customItemList), [customItemList, getCustomItemTotal])
  const invoicePendingToSubmit = useRevertedInvoice((store) => store.revertedInvoice)

  // CAR-3893: the app shows an invoice before the client rebate; only the cost PDF and QuickBooks carry it.
  const totalCost = useMemo(
    () => packagesTotal + servicesTotal + partsTotal + laborTotal + customItemTotal,
    [packagesTotal, servicesTotal, partsTotal, laborTotal, customItemTotal]
  )

  const revertToWO = useCallback(async () => {
    try {
      const revertToWOResponse = await revertToWorkOrder({
        invoiceId: invoiceData.id
      })

      if (revertToWOResponse.status === 200) {
        if (typeof setRevertedInvoice === 'function') {
          setRevertedInvoice(invoiceData)
        }
        history.push(getWorkOrderSummaryUrl(invoiceData.reference_number), { dataFromInvoiceScreen: true })
      }
    } catch (error) {
      console.error('ERROR', error)
      addMessage({ text: getFriendlyErrorMessage(error) })
    }
  }, [invoiceData, history, setRevertedInvoice, addMessage])

  const modalContinue = useCallback(() => {
    setShowModal(false)
    revertToWO()
  }, [revertToWO])

  const saveNote = useCallback(
    async (notes) => {
      const isSuccess = (status) => status === 200 || status === 201

      if (notes.noteType === 'client_note') {
        const clientNotes = notes?.note?.client_notes
        const updatedClientData = {
          ...wo.client,
          notes: clientNotes
        }

        const { status, data } = await updateClient({ data: updatedClientData })

        if (isSuccess(status)) {
          setNotes((prevNotes) => ({
            ...prevNotes,
            client_notes: data.notes
          }))
        }
      } else {
        const { status, data } = await updateNote({
          type: 'workorders',
          id: wo.id,
          data: notes
        })

        if (isSuccess(status)) {
          const updatedWorkOrder = data.workOrder

          setNotes((prevNotes) => ({
            ...prevNotes,
            notes_external: updatedWorkOrder.notes_external,
            notes_internal: updatedWorkOrder.notes_internal,
            client_notes: wo.client?.notes
          }))
        }
      }
    },
    [wo.id, wo.client]
  )

  const displayPDFContent = (pdfContent, fileName) => {
    const fileValue = new Blob([pdfContent], { type: 'application/pdf' }, fileName)
    fileValue.id = invoiceData.id
    const fileURL = URL.createObjectURL(fileValue)
    const pdfWindow = window.open()
    if (pdfWindow === null) {
      alert('Pop-up was blocked by your browser! Please allow pop-ups for caradasplus.com to view Invoice.')
    } else {
      pdfWindow.location.href = fileURL
    }
  }

  // `variant` is INVOICE_PDF_VARIANT.RETAIL or .COST — retail is the unmarked document with
  // custom discounts but no client rebate; cost adds the rebate, watermarked "Cost Invoice".
  // View, print or download an invoice PDF (all users, client users included).
  const viewPdfHandler = useCallback(async (invoice, variant = INVOICE_PDF_VARIANT.RETAIL, action = PDF_ACTION.VIEW) => {
    try {
      setPdfLoader(true)
      const invoiceId = invoice.id
      const invoicePdfResponse = await getInvoicePdf({
        invoiceId: invoiceId,
        variant
      })
      if (invoicePdfResponse.status === 200) {
        handlePdf(invoicePdfResponse.data, `invoice_${invoice.reference_number}_${variant}.pdf`, action)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { invoice, variant, action, path: 'InvoiceSummaryPage/viewPdf' } })
    } finally {
      setPdfLoader(false)
    }
  }, [])

  const viewCOCPdf = useCallback(async (invoice) => {
    try {
      setPdfLoader(true)
      const invoiceId = invoice.id
      const invoicePdfResponse = await getInvoiceCertificateOfCalibration({
        invoiceId: invoiceId
      })
      if (invoicePdfResponse.status === 200) {
        displayPDFContent(invoicePdfResponse.data, `certificate_${invoiceId}.pdf`)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { invoice, path: 'InvoiceSummaryPage/viewCOC' } })
    } finally {
      setPdfLoader(false)
    }
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      if (isScored) {
        try {
          const invoiceResponse = await getInvoiceByReferenceNumber({ referenceNumber })
          invoiceResponse.data.invoiceDto.safetySystemsItems = invoiceResponse.data.safetySystemsItems
          invoiceResponse.data.invoiceDto.calibrations = invoiceResponse.data.qwiCalibrations
          invoiceResponse.data.invoiceDto.services = invoiceResponse.data.qwiServices
          invoiceResponse.data.invoiceDto.parts = invoiceResponse.data.qwiParts
          invoiceResponse.data.invoiceDto.labours = invoiceResponse.data.qwiLabours
          invoiceResponse.data.invoiceDto.customItems = invoiceResponse.data.customItems
          setIsServiceOnly(invoiceResponse.data.invoiceDto.type === 'SERVICE_ONLY')
          setInvoiceData(invoiceResponse.data.invoiceDto)
        } catch (error) {
          console.error(error)
        }
      }
    }

    fetchData()
  }, [referenceNumber, isScored])

  const fetchData = async () => {
    try {
      setLoading(true)

      const getInvoiceData = async () => {
        const invoiceResponse = await getInvoiceByReferenceNumber({ referenceNumber })
        if (invoiceResponse && invoiceResponse.status === 200) {
          const data = invoiceResponse.data
          const { invoiceDto } = data
          if (!data.invoiceDto) {
            redirectToForbiddenPage()
            return
          }
          setInvoiceData(data.invoiceDto)
          const qwi_response = await getQWIByInvoiceId(invoiceDto?.id)
          setQWIData(qwi_response.data)
          setWo(data.workOrderDto)
          setNotes({
            notes_external: data.workOrderDto.notes_external || '',
            notes_internal: data.workOrderDto.notes_internal || '',
            client_notes: data.workOrderDto.client?.notes || ''
          })

          data.invoiceDto.safetySystemsItems = data.safetySystemsItems
          data.invoiceDto.calibrations = data.qwiCalibrations
          data.invoiceDto.services = data.qwiServices
          data.invoiceDto.parts = data.qwiParts
          data.invoiceDto.labours = data.qwiLabours
          data.invoiceDto.customItems = data.qwiCustomItems

          setIsServiceOnly(data.invoiceDto.type === 'SERVICE_ONLY')

          const flattenAndSet = (items, type, setter) => {
            const flattened = items.map((item) => flattenItem(item, type))
            setter(flattened)
          }

          if (data?.qwiCalibrations?.length > 0) {
            flattenAndSet(data.qwiCalibrations, 'calibration', setPackages)
          }

          if (data?.qwiServices?.length > 0) {
            flattenAndSet(data.qwiServices, 'service', setServices)
          }

          if (data?.qwiParts?.length > 0) {
            flattenAndSet(data.qwiParts, 'part', setParts)
          }

          if (data?.qwiLabours?.length > 0) {
            flattenAndSet(data.qwiLabours, 'labour', setLabor)
          }

          if (data.qwiCustomItems?.length > 0) {
            const updatedList = normalizeCustomItems(data.qwiCustomItems)
            // The client rebate line is left out: only the cost invoice PDF and QuickBooks
            // carry it (CAR-3893).
            setCustomItemList(splitCustomItems(updatedList).standardItems)
          } else {
            setCustomItemList([])
          }
        } else {
          redirectToForbiddenPage()
          return
        }
      }

      await getInvoiceData()
    } catch (error) {
      Logger.error({
        message: error,
        payload: {
          ccid: invoiceData.calibrationCenter?.id,
          referenceNumber
        }
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (referenceNumber) fetchData()
  }, [referenceNumber, invoicePendingToSubmit])

  // Email Invoice runs in two steps: pick the document(s) to send, then the email form.
  // Nothing is fetched until a choice is made, so cancelling the picker costs no calls.
  const emailInvoiceHandler = useCallback(() => {
    setIsEmailVariantPickerOpen(true)
  }, [])

  // `choice` is an INVOICE_EMAIL_VARIANT_CHOICE: retail, cost, or both. It decides which
  // invoice PDFs are attached and which variants the backend is told to send.
  const startEmailInvoice = useCallback(
    async (choice) => {
      const variants = invoiceVariantsForEmailChoice(choice)
      setIsEmailVariantPickerOpen(false)
      setEmailVariants(variants)
      // Drop any attachment left over from a previous choice so the form can't briefly
      // show the wrong document while the new PDFs load.
      setInvoicePdfs([])

      setLoading(true)
      try {
        const emailContactResponse = await getInvoiceEmails({ invoiceId: invoiceData.id })
        if (emailContactResponse.status === 200) {
          setIsEmail(true)
          setEmailContacts(emailContactResponse.data)
        }
        const responses = await Promise.all(variants.map((variant) => getInvoicePdf({ invoiceId: invoiceData.id, variant })))
        const files = responses.map((response, index) => {
          if (response?.status !== 200) return null
          const fileValue = new File([response.data], `invoice_${invoiceData.reference_number}_${variants[index]}.pdf`)
          fileValue.id = invoiceData.id
          return fileValue
        })
        // All-or-nothing: a partial set would let SEND go out missing a document the
        // user asked for.
        if (files.every(Boolean)) setInvoicePdfs(files)
      } catch (error) {
        Logger.error({
          message: error,
          payload: { InvoiceId: invoiceData.id, choice, path: 'InvoiceSummaryPage/startEmailInvoice' }
        })
      } finally {
        setLoading(false)
      }
    },
    [invoiceData, setLoading]
  )

  const ErrorMessages = (messagesMap) => {
    return (
      <AdasBox className='m-0'>
        {messagesMap.map((msg, index) => (
          <AdasTypography
            key={`msg_${index}`}
            variant='body1'
            className='text-error font-normal text-base text-left leading-5 my-[20px] mx-0 tracking-[0.15000000596046448px]'
          >
            {msg}
          </AdasTypography>
        ))}
      </AdasBox>
    )
  }

  const handleReopen = useCallback(async () => {
    try {
      const reopenInvoiceResponse = await reOpenInvoices({
        invoiceIds: [invoiceData.id]
      })

      if (reopenInvoiceResponse.status === 200) {
        history.push('/invoices')
      }
    } catch (error) {
      console.error('ERROR', error)
    }
  }, [invoiceData.id, history])

  const ValidateAndReopenModal = () => {
    const restriction = getReopenRestriction(user, invoiceData?.closedAt)
    if (restriction === 'blocked') {
      setShowNotifyError(true)
    } else if (restriction === 'warn') {
      setShowWarningError(true)
    }
    setShowReopenModal(true)
  }

  const reOpenModalContinue = useCallback(() => {
    closeReopenModal()
    handleReopen()
  }, [handleReopen])

  const closeReopenModal = () => {
    setShowNotifyError(false)
    setShowWarningError(false)
    setShowReopenModal(false)
  }

  const isInvoiceOpen = StatusEnum[invoiceData.status] === StatusEnum.OPEN
  const isInvoiceClosed = StatusEnum[invoiceData.status] === StatusEnum.CLOSED
  const canManage = user.is_admin || user.is_owner || user.is_manager
  // Client users (read-only) view and download invoices; they never email them or raise support.
  const isReadOnly = useIsReadOnly()
  const canSendToQbo = canManage
  const canViewScorecard = (user.is_admin && isInvoiceClosed) || invoiceData?.quality_review?.is_completed
  const canReopen = canManage && isInvoiceClosed
  const canRevertToWO = isInvoiceOpen && canManage

  const vehicleData = useMemo(() => wo?.vehicle || {}, [wo])

  // Calibration completion flags can live on either the invoice or the source WO
  const isPreCalibrationComplete = invoiceData.is_precalibration_complete || wo?.is_precalibration_complete
  const isCalibrationStepComplete = invoiceData.is_calibration_step_complete || wo?.is_calibration_step_complete
  const isPostCalibrationComplete = invoiceData.is_postcalibration_complete || wo?.is_postcalibration_complete
  const completeStepCount = [isPreCalibrationComplete, isCalibrationStepComplete, isPostCalibrationComplete].filter(Boolean).length

  const onSaveCustomItem = (items) => {
    const normalizedItems = normalizeCustomItems(items)
    setCustomItemList(splitCustomItems(normalizedItems).standardItems)
    fetchData()
  }

  useEffect(() => {
    if (QWIData?.referralSourceCode) {
      const fetchReferralData = async () => {
        // Fetch referral data if referralSourceCode is present
        const referral_response = await getReferralByCode({
          ccId: invoiceData?.calibrationCenter?.id,
          code: QWIData?.referralSourceCode
        })
        if (referral_response?.data) setReferralData(referral_response.data)
      }
      fetchReferralData()
    }
  }, [QWIData?.referralSourceCode, invoiceData?.calibrationCenter?.id])

  const [actionsOpen, setActionsOpen] = useState(false)
  const actionsRef = useRef(null)
  useEffect(() => {
    if (!actionsOpen) return
    const handler = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) setActionsOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [actionsOpen])

  // Register the Actions menu with the global mobile header so it replaces the
  // search/filter icons on this page. Items mirror the desktop dropdown.
  const setPageAction = useMobileToolbarStore((s) => s.setPageAction)
  const clearPageAction = useMobileToolbarStore((s) => s.clearPageAction)
  useEffect(() => {
    // Shared order on Quote, WO and Invoice (CAR-4070): Email, View / Print / Download, page steps, Support.
    const items = [
      ...(isReadOnly ? [] : [{ label: 'Email Invoice', icon: Mail, onClick: () => emailInvoiceHandler() }]),
      ...pdfActionItems('Retail Invoice', (action) => viewPdfHandler(invoiceData, INVOICE_PDF_VARIANT.RETAIL, action)),
      { label: 'View Cost Invoice', icon: FileText, onClick: () => viewPdfHandler(invoiceData, INVOICE_PDF_VARIANT.COST) },
      { label: 'View Certificate', icon: FileSignature, onClick: () => viewCOCPdf(invoiceData) },
      ...(canRevertToWO ? [{ label: 'Edit Invoice', icon: RotateCcw, onClick: () => setShowModal(true) }] : []),
      ...(canReopen ? [{ label: 'Reopen Invoice', icon: RotateCcw, onClick: () => ValidateAndReopenModal() }] : []),
      ...(canViewScorecard ? [{ label: 'Scorecard', icon: ClipboardCheck, onClick: () => setIsScorecardOpen(true) }] : []),
      ...(isReadOnly ? [] : [{ label: 'Support', icon: HelpCircle, onClick: () => setSupportOpen(true) }])
    ]
    setPageAction({
      label: 'Actions',
      disabled: !invoiceData?.id,
      busy: pdfLoader,
      items
    })
    return () => clearPageAction()
  }, [
    invoiceData,
    pdfLoader,
    canReopen,
    canRevertToWO,
    isReadOnly,
    canViewScorecard,
    canSendToQbo,
    emailInvoiceHandler,
    viewPdfHandler,
    viewCOCPdf,
    history,
    referenceNumber,
    setPageAction,
    clearPageAction
  ])

  const renderCreatedDate = () => {
    if (!invoiceData.created_at) return null
    const d = new Date(invoiceData.created_at)
    const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    return `${date} ${time}`
  }

  return (
    <AdasContainer maxWidth={false}>
      {invoiceData.id && (
        <AdasBox className='mb-[60px] mt-5'>
          <InvoiceEmailVariantModal
            open={isEmailVariantPickerOpen}
            onClose={() => setIsEmailVariantPickerOpen(false)}
            onContinue={startEmailInvoice}
          />
          <EmailForm
            emailType='invoice'
            invoicePdfs={invoicePdfs}
            emailVariants={emailVariants}
            isOpen={isEmail}
            contacts={emailContacts}
            emailData={invoiceData}
            onClose={() => setIsEmail(false)}
          />
          {canViewScorecard && (
            <ScoreDrawer
              referenceNumber={invoiceData.reference_number}
              invoiceId={invoiceData.id}
              roNumber={invoiceData.repair_order_num}
              isOpen={isScorecardOpen}
              setIsScored={setIsScored}
              onClose={() => {
                setIsScorecardOpen(false)
              }}
            />
          )}

          {/* Mobile vehicle card — meta + status pill, image, then title/total/client */}
          <AdasBox className='mb-3 min-[435px]:hidden rounded-xl border border-[#D1D5D8] bg-white p-4'>
            <AdasBox className='mb-3 flex items-center justify-between gap-3'>
              <AdasTypography className='text-[12px] text-[#2C3D4D]'>
                <span>Invoice# {invoiceData.reference_number}</span>
                {invoiceData.repair_order_num && (
                  <>
                    <span className='mx-1.5 text-[#CBD5E1]'>·</span>
                    <span>RO# {invoiceData.repair_order_num}</span>
                  </>
                )}
                {isServiceOnly && (
                  <>
                    <span className='mx-1.5 text-[#CBD5E1]'>·</span>
                    <span>Service-Only</span>
                  </>
                )}
                {invoiceData.insurance_company?.name && (
                  <>
                    <span className='mx-1.5 text-[#CBD5E1]'>·</span>
                    <span>{invoiceData.insurance_company.name}</span>
                  </>
                )}
              </AdasTypography>
            </AdasBox>
            <AdasBox className='flex h-[140px] items-center justify-center overflow-hidden rounded-md bg-white text-[#6B7280]'>
              {vehicleData?.image_url ? (
                <AdasImage src={vehicleData.image_url} alt='Vehicle' className='h-full w-full object-contain' />
              ) : (
                <CarImageHolder showText={false} />
              )}
            </AdasBox>
            <AdasBox className='mt-3 text-center'>
              <AdasTypography className='text-[15px] font-bold uppercase tracking-tight text-[#0A1628]'>
                {vehicleData?.year
                  ? `${vehicleData.year} ${vehicleData.make} ${vehicleData.model} ${vehicleData.trim || ''}`.trim()
                  : 'Vehicle'}
              </AdasTypography>
              <AdasTypography className='mt-1 text-[14px] font-semibold text-[#0A1628]'>
                Total: {currencyFormatter(totalCost)}
              </AdasTypography>
              <AdasTypography className='mt-0.5 text-[12px] text-[#2C3D4D]'>
                {invoiceData.client?.name && <span>{invoiceData.client.name}</span>}
              </AdasTypography>
              {invoiceData.created_at && (
                <AdasTypography className='mt-1 text-[11px] text-[#2C3D4D]'>Created {renderCreatedDate()}</AdasTypography>
              )}
              <AdasBox className='mt-2 flex justify-center' onClick={(e) => e.stopPropagation()}>
                <AssigneeAvatarPicker
                  type='invoices'
                  workOrderId={invoiceData.id}
                  initialAssignee={invoiceData.assigned_to}
                  closedStatus={true}
                  showName
                  showEditIcon
                />
              </AdasBox>
              {invoiceData.quality_review?.is_completed && (
                <AdasBox className='mt-2 inline-flex items-center justify-center gap-1 rounded-md bg-[#ECFDF5] px-2 py-1 text-[11px] font-semibold text-[#047857]'>
                  <CheckCheck className='h-3 w-3' />
                  Scorecard
                </AdasBox>
              )}
            </AdasBox>
          </AdasBox>

          {/* Page heading — Back link + page title + assignee + Actions menu */}
          <AdasBox className='mb-4 hidden min-[435px]:flex flex-wrap items-end justify-between gap-4'>
            <AdasBox>
              <button
                type='button'
                onClick={() => history.goBack()}
                className='mb-1 text-[13px] font-medium text-[#1F6FEB] hover:underline'
              >
                Back to Invoices
              </button>
              <h1 className='text-[28px] font-bold leading-tight text-[#0A1628]'>Invoice Summary</h1>
            </AdasBox>
            <AdasBox className='flex items-center gap-3'>
              <AssigneeAvatarPicker
                type='invoices'
                workOrderId={invoiceData.id}
                initialAssignee={invoiceData.assigned_to}
                closedStatus={true}
                showName
                label='Assignee:'
              />
              {/* Generating a PDF is a server round trip, so show progress beside the
                  Actions button rather than leaving the click with no feedback. */}
              {pdfLoader && <AdasCircularProgress size={20} className='text-[#0A1628]' aria-label='Generating PDF' />}
              <AdasBox ref={actionsRef} className='relative'>
                <button
                  type='button'
                  onClick={() => setActionsOpen((o) => !o)}
                  className='inline-flex items-center gap-2 rounded-lg bg-[#0A1628] px-5 py-2.5 text-[13px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#1F2D3D]'
                >
                  Actions
                  {actionsOpen ? <ChevronUp className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
                </button>
                {actionsOpen && (
                  <AdasBox className='absolute right-0 top-full z-30 mt-2 w-[260px] overflow-hidden rounded-lg border border-[#EBEDF0] bg-white py-2 shadow-lg'>
                    {/* Shared order on Quote, WO and Invoice (CAR-4070): Email, View / Print / Download, page steps, Support. */}
                    {!isReadOnly && (
                      <button
                        type='button'
                        onClick={() => {
                          setActionsOpen(false)
                          emailInvoiceHandler()
                        }}
                        className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                      >
                        <Mail className='h-4 w-4 text-[#475569]' />
                        Email Invoice
                      </button>
                    )}
                    <PdfActionMenuItems
                      noun='Retail Invoice'
                      onAction={(action) => viewPdfHandler(invoiceData, INVOICE_PDF_VARIANT.RETAIL, action)}
                      onClose={() => setActionsOpen(false)}
                    />
                    <button
                      type='button'
                      onClick={() => {
                        setActionsOpen(false)
                        viewPdfHandler(invoiceData, INVOICE_PDF_VARIANT.COST)
                      }}
                      className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                    >
                      <FileText className='h-4 w-4 text-[#475569]' />
                      View Cost Invoice
                    </button>
                    <button
                      type='button'
                      onClick={() => {
                        setActionsOpen(false)
                        viewCOCPdf(invoiceData)
                      }}
                      className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                    >
                      <FileSignature className='h-4 w-4 text-[#475569]' />
                      View Certificate
                    </button>
                    {canRevertToWO && (
                      <button
                        type='button'
                        onClick={() => {
                          setActionsOpen(false)
                          setShowModal(true)
                        }}
                        className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-foreground transition-colors hover:bg-[#F8FAFC]'
                      >
                        <RotateCcw className='h-4 w-4 text-[#475569]' />
                        Edit Invoice
                      </button>
                    )}
                    {canReopen && (
                      <button
                        type='button'
                        onClick={() => {
                          setActionsOpen(false)
                          ValidateAndReopenModal()
                        }}
                        className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                      >
                        <RotateCcw className='h-4 w-4 text-[#475569]' />
                        Reopen Invoice
                      </button>
                    )}
                    {canViewScorecard && (
                      <button
                        type='button'
                        onClick={() => {
                          setActionsOpen(false)
                          setIsScorecardOpen(true)
                        }}
                        className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                      >
                        <ClipboardCheck className='h-4 w-4 text-[#475569]' />
                        Scorecard
                      </button>
                    )}
                    {!isReadOnly && (
                      <button
                        type='button'
                        onClick={() => {
                          setActionsOpen(false)
                          setSupportOpen(true)
                        }}
                        className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                      >
                        <HelpCircle className='h-4 w-4 text-[#475569]' />
                        Support
                      </button>
                    )}
                  </AdasBox>
                )}
              </AdasBox>
            </AdasBox>
          </AdasBox>

          {/* Vehicle summary card — desktop: thumbnail · title/VIN/client+insurance · Invoice#/RO# + date */}
          <AdasBox className='mb-4 hidden min-[435px]:block rounded-xl border border-[#D1D5D8] bg-white p-4'>
            <AdasBox className='flex items-stretch justify-between gap-4'>
              <AdasBox className='flex items-start gap-4'>
                <AdasBox className='flex h-[100px] w-[160px] flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-[#EBEDF0] text-[#6B7280]'>
                  {vehicleData?.image_url ? (
                    <AdasImage src={vehicleData.image_url} alt='Vehicle' className='h-full w-full object-cover' />
                  ) : (
                    <CarImageHolder showText={false} />
                  )}
                </AdasBox>
                <AdasBox className='min-w-0 flex-1'>
                  <AdasBox className='flex items-center gap-2'>
                    <AdasTypography className='text-[18px] font-bold leading-tight text-[#0A1628]'>
                      {vehicleData?.year
                        ? `${vehicleData.year} ${vehicleData.make} ${vehicleData.model} ${vehicleData.trim || ''}`.trim()
                        : 'Vehicle'}
                    </AdasTypography>
                    {invoiceData.quality_review?.is_completed && (
                      <span className='inline-flex items-center gap-1 rounded-md bg-[#ECFDF5] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#047857]'>
                        <CheckCheck className='h-3 w-3' />
                        Scorecard
                      </span>
                    )}
                  </AdasBox>
                  {vehicleData?.vin && (
                    <AdasTypography className='mt-2 font-mono text-[13px] tracking-wider text-[#2C3D4D]'>{vehicleData.vin}</AdasTypography>
                  )}
                  <AdasBox className='mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-[#2C3D4D]'>
                    {invoiceData.client?.name && <span>{invoiceData.client.name}</span>}
                    {invoiceData.insurance_company?.name && (
                      <>
                        <span className='text-[#D1D5D8]'>·</span>
                        <span>{invoiceData.insurance_company.name}</span>
                      </>
                    )}
                  </AdasBox>
                </AdasBox>
              </AdasBox>
              <AdasBox className='flex flex-col items-end whitespace-nowrap'>
                <AdasTypography className='text-[14px] leading-tight text-[#2C3D4D]'>
                  <span>Invoice# {invoiceData.reference_number}</span>
                  {invoiceData.repair_order_num && (
                    <>
                      <span className='mx-2 text-[#CBD5E1]'>|</span>
                      <span>RO# {invoiceData.repair_order_num}</span>
                    </>
                  )}
                </AdasTypography>
                {/* Spacer matches the VIN row height on the left so the date lines up with the client row */}
                <AdasBox className='mt-2 h-[18px]' aria-hidden='true' />
                {invoiceData.created_at && (
                  <AdasTypography className='mt-2 flex h-7 items-center text-[13px] text-[#2C3D4D]'>{renderCreatedDate()}</AdasTypography>
                )}
              </AdasBox>
            </AdasBox>
          </AdasBox>

          {!isLoading && (
            <AdasBox>
              {QWIData?.referralSourceCode && <ProductContractBanner referralData={referralData} />}
              {/* Static key for the Level 1 / 2 / 3A / 3B / 3C codes in calibration names, styled like the
                  NA banner above. Shown only when the record has an Auto Quote line and that banner isn't shown (CAR-4019). */}
              {shouldShowLevelLegend({
                packages,
                services,
                isServiceOnly,
                hasContract: hasContractBanner(referralData ?? { code: QWIData?.referralSourceCode })
              }) && <CalibrationLevelLegend />}

              {/* Line item sections */}
              {!isServiceOnly && (
                <>
                  <div className='hidden min-[435px]:block'>
                    <ExpandableSection
                      title='Calibrations'
                      total={packagesTotal}
                      items={packages}
                      manageLabel='MANAGE CALIBRATIONS'
                      disabled={!isActive}
                      canManage={canManage}
                      type={'calibration'}
                    />
                  </div>
                  <div className='min-[435px]:hidden'>
                    <MobileSection
                      title='Calibrations'
                      total={packagesTotal}
                      items={packages}
                      manageLabel='MANAGE CALIBRATIONS'
                      disabled={!isActive}
                      type='calibration'
                    />
                  </div>
                </>
              )}
              <div className='hidden min-[435px]:block'>
                <ExpandableSection
                  title='Services'
                  total={servicesTotal}
                  items={services}
                  manageLabel='MANAGE SERVICES'
                  disabled={!isActive}
                  canManage={canManage}
                  type={'service'}
                />
              </div>
              <div className='min-[435px]:hidden'>
                <MobileSection
                  title='Services'
                  total={servicesTotal}
                  items={services}
                  manageLabel='MANAGE SERVICES'
                  disabled={!isActive}
                  type='service'
                />
              </div>
              <div className='hidden min-[435px]:block'>
                <ExpandableSection
                  key='Parts'
                  title='Parts'
                  total={partsTotal}
                  items={parts}
                  manageLabel='MANAGE PARTS'
                  disabled={!isActive}
                  type={'part'}
                />
              </div>
              <div className='min-[435px]:hidden'>
                <MobileSection
                  title='Parts'
                  total={partsTotal}
                  items={parts}
                  manageLabel='MANAGE PARTS'
                  disabled={!isActive}
                  defaultExpanded={false}
                  type='part'
                />
              </div>
              <div className='hidden min-[435px]:block'>
                <ExpandableSection
                  key='Labour'
                  title='Labor'
                  total={laborTotal}
                  items={labor}
                  manageLabel='MANAGE LABOR'
                  disabled={!isActive}
                  type={'labor'}
                />
              </div>
              <div className='min-[435px]:hidden'>
                <MobileSection
                  title='Labor'
                  total={laborTotal}
                  items={labor}
                  manageLabel='MANAGE LABOR'
                  disabled={!isActive}
                  defaultExpanded={false}
                  type='labor'
                />
              </div>
              {canManage ? (
                <CustomItem
                  customItemTotal={customItemTotal}
                  existingCustomItemList={customItemList}
                  workOrderData={invoiceData}
                  onSaveCustomItem={onSaveCustomItem}
                  disabled={!isActive}
                />
              ) : (
                <AdasBox className='m-[20px]'>
                  <span>
                    <b>Custom Charges & Discount</b>
                  </span>
                  <span style={{ float: 'right' }}>
                    <b>{currencyFormatter(customItemTotal)}</b>
                  </span>
                  <ItemList itemList={customItemList} name='additional items' isCustomItem={false} />
                </AdasBox>
              )}

              {/* Sticky Total Amount card (desktop only) */}
              <AdasBox className='mt-4 hidden min-[435px]:flex sticky bottom-3 z-20 items-center justify-between rounded-lg border border-[#D1D5D8] bg-white px-4 py-3 shadow-md'>
                <AdasTypography className='text-[14px] font-semibold uppercase tracking-wide text-[#2C3D4D]'>Total Amount</AdasTypography>
                <AdasTypography className='text-[20px] font-bold text-[#0A1628]'>{currencyFormatter(totalCost)}</AdasTypography>
              </AdasBox>

              {/* Notes */}
              <Note
                existingExternalNote={notes.notes_external}
                existingInternalNote={notes.notes_internal}
                existingClientNote={notes.client_notes}
                submit={(props) => saveNote(props)}
              />

              {/* Work Order Documentation — backward-compat block, anchored to the bottom */}
              {!isServiceOnly && (
                <AdasBox className='mt-6'>
                  <AdasTypography className='mb-2 text-[20px] font-bold text-[#0A1628]'>Work Order Documentation</AdasTypography>
                  <AdasBox className='overflow-hidden rounded-lg border border-[#D1D5D8] bg-white divide-y divide-[#EBEDF0]'>
                    <WoDocStepRow
                      label='Pre Calibration Set Up Complete'
                      complete={!!isPreCalibrationComplete}
                      onClick={redirectPreCalibration}
                    />
                    <WoDocStepRow
                      label='Calibration Documentation Complete'
                      complete={!!isCalibrationStepComplete}
                      onClick={redirectCalibration}
                    />
                    <WoDocStepRow
                      label='Post Calibration Complete'
                      complete={!!isPostCalibrationComplete}
                      onClick={redirectPostCalibration}
                    />
                  </AdasBox>
                </AdasBox>
              )}

              {/* Sub-page nav strip — Vehicle Info / Photos / Documents (matches WO Summary) */}
              <AdasBox className='mt-4 flex flex-nowrap items-center gap-2 overflow-x-auto'>
                <button
                  type='button'
                  onClick={redirectVehicleInfo}
                  className='inline-flex flex-1 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-[#D1D5D8] bg-white px-3 py-2 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                >
                  <Info className='h-4 w-4 shrink-0' />
                  Vehicle Info
                </button>
                <button
                  type='button'
                  onClick={redirectPhotos}
                  className='inline-flex flex-1 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-[#D1D5D8] bg-white px-3 py-2 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                >
                  <Camera className='h-4 w-4 shrink-0' />
                  Photos
                </button>
                <button
                  type='button'
                  onClick={redirectDocuments}
                  className='inline-flex flex-1 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-[#D1D5D8] bg-white px-3 py-2 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                >
                  <FileText className='h-4 w-4 shrink-0' />
                  Documents
                </button>
              </AdasBox>
            </AdasBox>
          )}

          {/* Mobile spacer */}
          <AdasBox className='min-[435px]:hidden h-[20px]' aria-hidden='true' />

          {/* Bottom action footer (desktop) */}
          <AdasBox className='mt-6 hidden min-[435px]:flex flex-wrap items-center justify-between gap-3 border-t border-[#EBEDF0] pt-4'>
            <button
              type='button'
              onClick={() =>
                history.push({
                  pathname: '/invoices',
                  search: `?status=${invoiceData.status || 'OPEN'}`
                })
              }
              className='inline-flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-wide text-[#0A1628] hover:underline'
            >
              <ArrowLeft className='h-4 w-4' />
              Invoices
            </button>
          </AdasBox>

          {/* Modals */}
          {/* Edit Invoice: the standard pop-up (FormModal), laid out like "Appointment Already Scheduled" and
              VIN Match Found (CAR-4047) - outlined Cancel focused first, navy Confirm. */}
          <FormModal
            open={showModal}
            onClose={() => setShowModal(false)}
            icon={RotateCcw}
            eyebrow={referenceNumber ? `Invoice #${referenceNumber}` : undefined}
            title={GENERIC_MESSAGES.REVERT_TO_WO_TITLE}
            footer={<FormModalFooter onCancel={() => setShowModal(false)} onConfirm={modalContinue} confirmLabel='Confirm' />}
          >
            <p className='text-[13px] leading-5 text-[#334155]'>{GENERIC_MESSAGES.REVERT_TO_WO}</p>
          </FormModal>
          <ConfirmModal
            open={showReopenModal}
            handleClose={closeReopenModal}
            handleContinue={reOpenModalContinue}
            title={showNotifyError ? GENERIC_MESSAGES.UNABLE_TO_REOPEN_INVOICE_TITLE : GENERIC_MESSAGES.REOPEN_INVOICE_TITLE}
            warningMessage={showWarningError && GENERIC_MESSAGES.INVOICES_CAN_REOPEN_WITH_IN_MONTH}
            message={
              showNotifyError
                ? ErrorMessages([
                    GENERIC_MESSAGES.INVOICES_CAN_REOPEN_WITH_IN_MONTH,
                    GENERIC_MESSAGES.INVOICES_CAN_REOPEN_ASSISTANCE_MESSAGE
                  ])
                : GENERIC_MESSAGES.CONFIRM_REOPEN_INVOICE
            }
            confirmLabel={BUTTONS_DISPLAY_LABEL.REOPEN_INVOICE_LABEL}
            typeCheck={GENERIC_MESSAGES.REOPEN_CODE}
            notifyError={showNotifyError}
          />

          <CreateSupportRequestModal open={supportOpen} onClose={() => setSupportOpen(false)} referenceNumber={referenceNumber} />
        </AdasBox>
      )}
    </AdasContainer>
  )
}
